/**
 * Primitivas visuales de la consola.
 *
 * Usan los MISMOS tokens que el ERP (`index.css`) pero con otra densidad: radios
 * chicos, filas de 2 lineas, micro-etiquetas en versalita y monoespaciada para
 * todo lo que sea un identificador tecnico. La idea es que quien entra sepa en
 * dos segundos que esta en la consola de administracion y no en la app.
 *
 * Lo que no se duplica se importa del shell (`Button`, `Badge`): un boton con
 * otro foco o otro tamano tactil seria una regresion de accesibilidad.
 */

import clsx from 'clsx';
import type { KeyboardEvent, ReactNode } from 'react';

import { IconAlert, IconSearch, IconSpinner } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';

// ---------------------------------------------------------------------------
// Superficies
// ---------------------------------------------------------------------------
export function Panel({
  title,
  subtitle,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={clsx('rounded-md border border-line bg-surface shadow-sm', className)}>
      {(title !== undefined || actions !== undefined) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <div className="min-w-0">
            {title !== undefined && <h2 className="truncate text-sm font-semibold">{title}</h2>}
            {subtitle !== undefined && <p className="mt-0.5 text-xs text-fg-muted">{subtitle}</p>}
          </div>
          {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={clsx('p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

/** Micro-titulo de seccion. Es un `<h3>` de verdad: el lector de pantalla navega por ellos. */
export function Etiqueta({
  children,
  as = 'h3',
}: {
  children: ReactNode;
  // `h5` para los subtitulos dentro de una tarjeta que ya usa `h4` (los bloques
  // de reglas de la ficha de rol): saltarse un nivel rompe el arbol de titulos
  // para quien navega con lector de pantalla.
  as?: 'h3' | 'h4' | 'h5' | 'p';
}) {
  const Tag = as;
  return (
    <Tag className="mb-2 text-2xs font-semibold uppercase tracking-[0.08em] text-fg-faint">{children}</Tag>
  );
}

/** Identificador tecnico: nombre logico de modelo, codigo de grupo, ruta de API. */
export function Codigo({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <code
      className={clsx(
        'rounded-sm bg-surface-raised px-1.5 py-0.5 font-mono text-2xs text-fg-muted',
        className,
      )}
    >
      {children}
    </code>
  );
}

/** Tarjeta de metrica del tablero de estado. */
export function Metrica({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'neutral' | 'accent' | 'warning' | 'danger';
}) {
  const borde =
    tone === 'accent'
      ? 'border-accent/40'
      : tone === 'warning'
        ? 'border-warning/40'
        : tone === 'danger'
          ? 'border-danger/40'
          : 'border-line';
  return (
    <div className={clsx('rounded-md border bg-surface px-4 py-3 shadow-sm', borde)}>
      <p className="text-2xs font-semibold uppercase tracking-[0.08em] text-fg-faint">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums leading-tight">{value}</p>
      {hint !== undefined && <p className="mt-1 text-xs text-fg-muted">{hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estados
// ---------------------------------------------------------------------------
export function Cargando({ label = 'Consultando…' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 py-8 text-sm text-fg-muted">
      <IconSpinner className="h-4 w-4" />
      <span>{label}</span>
    </div>
  );
}

export function Vacio({ title, description }: { title: string; description?: string }) {
  return (
    <div className="rounded-md border border-dashed border-line px-4 py-10 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description !== undefined && <p className="mx-auto mt-1 max-w-sm text-xs text-fg-muted">{description}</p>}
    </div>
  );
}

/**
 * Fallo con causa a la vista.
 *
 * `detail` es para el dato tecnico (la ruta que dio 404, el mensaje del backend).
 * Se muestra siempre: quien opera la consola es la persona que puede arreglarlo,
 * esconderle el detalle no la protege de nada.
 */
export function Fallo({
  title,
  message,
  detail,
  onRetry,
}: {
  title: string;
  message: string;
  detail?: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="rounded-md border border-danger/40 bg-danger-soft p-4">
      <div className="flex items-start gap-3">
        <IconAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-1 text-sm text-fg-muted">{message}</p>
          {detail !== undefined && detail !== '' && (
            <p className="mt-2 break-words">
              <Codigo>{detail}</Codigo>
            </p>
          )}
        </div>
        {onRetry !== undefined && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Reintentar
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controles
// ---------------------------------------------------------------------------
export function CampoBusqueda({
  value,
  onChange,
  placeholder,
  label,
  id,
}: {
  value: string;
  onChange: (valor: string) => void;
  placeholder?: string;
  /** Etiqueta accesible. Se dibuja arriba del campo salvo que `visuallyHidden`. */
  label: string;
  id: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-2xs font-semibold uppercase tracking-[0.08em] text-fg-faint">
        {label}
      </label>
      <div className="relative">
        <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
        <input
          id={id}
          type="search"
          value={value}
          placeholder={placeholder}
          onChange={(evento) => {
            onChange(evento.target.value);
          }}
          className="h-9 w-full rounded-md border border-line bg-surface pl-8 pr-3 text-sm placeholder:text-fg-faint hover:border-line-strong"
        />
      </div>
    </div>
  );
}

/**
 * Fila seleccionable de una lista maestra.
 *
 * `aria-current="true"` y no `aria-selected`: no es un listbox, es navegacion
 * dentro de la pantalla. Las flechas mueven el foco entre hermanos; ver
 * {@link useNavegacionPorFlechas}.
 */
export function FilaMaestra({
  selected,
  onSelect,
  children,
  className,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className={clsx(
          'w-full border-l-2 px-3 py-2.5 text-left transition-colors',
          selected
            ? 'border-l-accent bg-accent-soft'
            : 'border-l-transparent hover:bg-surface-raised focus-visible:bg-surface-raised',
          className,
        )}
      >
        {children}
      </button>
    </li>
  );
}

/**
 * Mueve el foco con flechas dentro de una lista de botones.
 *
 * Se cuelga del contenedor (`onKeyDown`) en vez de por fila: con 40 roles, tabular
 * uno por uno para llegar al ultimo es inoperable con teclado.
 */
export function useNavegacionPorFlechas() {
  return (evento: KeyboardEvent<HTMLElement>) => {
    if (evento.key !== 'ArrowDown' && evento.key !== 'ArrowUp') return;
    const contenedor = evento.currentTarget;
    const botones = [...contenedor.querySelectorAll<HTMLButtonElement>('button')];
    const actual = botones.indexOf(document.activeElement as HTMLButtonElement);
    if (actual === -1) return;
    evento.preventDefault();
    const siguiente = evento.key === 'ArrowDown' ? actual + 1 : actual - 1;
    botones[(siguiente + botones.length) % botones.length]?.focus();
  };
}

/** Casilla de verificacion con descripcion, usada para asignar roles y empresas. */
export function Casilla({
  checked,
  onChange,
  disabled = false,
  title,
  description,
  aside,
}: {
  checked: boolean;
  onChange: (valor: boolean) => void;
  disabled?: boolean;
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 transition-colors',
        checked ? 'border-accent/40 bg-accent-soft' : 'border-line bg-surface hover:bg-surface-raised',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(evento) => {
          onChange(evento.target.checked);
        }}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--accent))]"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        {description !== undefined && <span className="mt-0.5 block text-xs text-fg-muted">{description}</span>}
      </span>
      {aside !== undefined && <span className="shrink-0">{aside}</span>}
    </label>
  );
}
